import React, { useState, useEffect, useRef } from "react";
import "./SignupVideoCarousel.css";

function SignupVideoCarousel({ videos = [] }) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [mutedStates, setMutedStates] = useState({});
  const videoRefs = useRef([]);
  const touchStartX = useRef(0);
  const touchEndX = useRef(0);

  // Handle video playback - mute, autoplay, and stop at 30 seconds
  useEffect(() => {
    if (videos.length === 0) return;

    const currentVideo = videoRefs.current[currentIndex];
    if (!currentVideo) return;

    // Set mute state and play current video
    const isMuted = mutedStates[currentIndex] !== false; // Default to true (muted)
    currentVideo.muted = isMuted;
    currentVideo.currentTime = 0;

    const playPromise = currentVideo.play();
    if (playPromise !== undefined) {
      playPromise.catch((error) => {
        console.error("Autoplay error:", error);
      });
    }

    // Pause other videos
    videoRefs.current.forEach((video, index) => {
      if (video && index !== currentIndex) {
        video.pause();
        video.currentTime = 0;
      }
    });

    // Handle timeupdate to stop at 30 seconds
    const handleTimeUpdate = () => {
      if (currentVideo.currentTime >= 30) {
        currentVideo.pause();
        // Auto-advance to next video after reaching 30 seconds
        setCurrentIndex((prevIndex) => (prevIndex + 1) % videos.length);
      }
    };

    currentVideo.addEventListener("timeupdate", handleTimeUpdate);

    return () => {
      currentVideo.removeEventListener("timeupdate", handleTimeUpdate);
    };
  }, [currentIndex, videos, mutedStates]);

  const handleDotClick = (index) => {
    setCurrentIndex(index);
  };

  const toggleMute = (index) => {
    const currentVideo = videoRefs.current[index];
    if (currentVideo) {
      const newMutedState = !currentVideo.muted;
      currentVideo.muted = newMutedState;
      setMutedStates((prev) => ({
        ...prev,
        [index]: newMutedState,
      }));
    }
  };

  const handleTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = (e) => {
    touchEndX.current = e.changedTouches[0].clientX;
    handleSwipe();
  };

  const handleSwipe = () => {
    const swipeDistance = touchStartX.current - touchEndX.current;
    const minSwipeDistance = 50; // Minimum distance for a swipe

    if (Math.abs(swipeDistance) > minSwipeDistance) {
      if (swipeDistance > 0 && currentIndex < videos.length - 1) {
        // Swiped left - go to next video
        setCurrentIndex(currentIndex + 1);
      } else if (swipeDistance < 0 && currentIndex > 0) {
        // Swiped right - go to previous video
        setCurrentIndex(currentIndex - 1);
      }
    }
  };

  if (!videos || videos.length === 0) {
    return (
      <div className="signup-carousel-error">
        <p>No videos available</p>
      </div>
    );
  }

  return (
    <div className="signup-carousel">
      <div
        className="signup-carousel-container"
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
      >
        <div
          className="signup-carousel-track"
          style={{
            transform: `translateX(-${currentIndex * 100}%)`,
          }}
        >
          {videos.map((video, index) => (
            <div key={index} className="signup-carousel-item">
              <video
                ref={(el) => (videoRefs.current[index] = el)}
                className="signup-video-player"
                playsInline
                muted
                poster={video.poster}
              >
                <source src={video.url} type="application/x-mpegURL" />
              </video>
              <button
                className="signup-mute-button"
                onClick={() => toggleMute(index)}
                aria-label={
                  mutedStates[index] !== false ? "Unmute video" : "Mute video"
                }
              >
                {mutedStates[index] !== false ? (
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                    width="24"
                    height="24"
                  >
                    <path d="M3.63 3.63a.996.996 0 0 0 0 1.41L7.29 8.7 7 9H4c-.55 0-1 .45-1 1v4c0 .55.45 1 1 1h3l3.29 3.29c.63.63 1.71.18 1.71-.71v-4.17l4.18 4.18c-.49.37-1.02.68-1.6.91-.36.15-.58.53-.58.92 0 .72.73 1.18 1.39.91.8-.33 1.55-.77 2.22-1.31l1.34 1.34a.996.996 0 1 0 1.41-1.41L5.05 3.63c-.39-.39-1.02-.39-1.42 0zM19 12c0 .82-.15 1.61-.41 2.34l1.53 1.53c.56-1.17.88-2.48.88-3.87 0-3.83-2.4-7.11-5.78-8.4-.59-.23-1.22.23-1.22.86v.19c0 .38.25.71.61.85C17.18 6.54 19 9.06 19 12zm-8.71-6.29-.17.17L12 7.76V6.41c0-.89-1.08-1.33-1.71-.7zM16.5 12A4.5 4.5 0 0 0 14 7.97v1.79l2.48 2.48c.01-.08.02-.16.02-.24z" />
                  </svg>
                ) : (
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                    width="24"
                    height="24"
                  >
                    <path d="M3 10v4c0 .55.45 1 1 1h3l3.29 3.29c.63.63 1.71.18 1.71-.71V6.41c0-.89-1.08-1.33-1.71-.71L7 9H4c-.55 0-1 .45-1 1zm13.5 2A4.5 4.5 0 0 0 14 7.97v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 4.45v.2c0 .38.25.71.6.85C17.18 6.53 19 9.06 19 12s-1.82 5.47-4.4 6.5c-.36.14-.6.47-.6.85v.2c0 .63.63 1.07 1.21.85C18.6 19.11 21 15.84 21 12s-2.4-7.11-5.79-8.4c-.58-.23-1.21.22-1.21.85z" />
                  </svg>
                )}
              </button>
              {video.title && video.artist && (
                <div className="signup-video-overlay">
                  <div className="signup-video-info">
                    <h3 className="signup-video-title">{video.title}</h3>
                    <p className="signup-video-artist">{video.artist}</p>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {videos.length > 1 && (
        <div className="signup-carousel-dots">
          {videos.map((_, index) => (
            <button
              key={index}
              className={`signup-carousel-dot ${
                index === currentIndex ? "active" : ""
              }`}
              onClick={() => handleDotClick(index)}
              aria-label={`Go to video ${index + 1}`}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export default SignupVideoCarousel;
